import { useMemo } from "react";
import { AdKitKey } from "types/enums";
import type { ClubhouseAdKitMetaDataDto } from "entities/responses/adkit";
import { useGetAdKitMetadataQuery } from "./api/endpoints/metadata";

/** Where the spot sits on a 16:9 sim screen, in percent — drawn as a highlighted box in thumbnails. */
export type PlacementScreenZone = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type PlacementCatalogEntry = {
  key: AdKitKey;
  name: string;
  screenZone: PlacementScreenZone;
  whereItShows: string;
  howItChanges: string;
  imageSize: string;
  /** width ÷ height the sim expects — what an image must match to "fit". */
  aspectRatio: number;
  /** Pixel size to export a crop at; undefined when the API gives none. */
  width?: number;
  height?: number;
  isHoleEnabled: boolean;
  canAssignToSpecificHoles: boolean;
  hasDynamicRotation: boolean;
  hasTransitions: boolean;
  defaultFadeIn?: number;
  defaultDisplay?: number;
  defaultFadeOut?: number;
};

// Product-approved names and "where it shows" copy, from the Ad Spaces PRD's
// placement reference table. The API's metadata.name/description are close,
// but these are the strings the design artboards use, so every screen reads
// the same regardless of what the backend happens to return.
const PLACEMENT_NAMES: Record<AdKitKey, string> = {
  [AdKitKey.TEE_SIGN_3D]: "3D Tee Sign",
  [AdKitKey.HOLE_LOAD_AD]: "Hole Load",
  [AdKitKey.SCORECARD_AD]: "Scorecard",
  [AdKitKey.SPONSOR_AD_3_IN_GAME]: "In-Game Corner",
  [AdKitKey.SPONSOR_AD_2_POST_SHOT]: "Post-Shot",
  [AdKitKey.SPONSOR_AD_1_SETUP]: "Setup Screens",
  [AdKitKey.RANGE_FAIRWAY_OVERLAY]: "Range Fairway",
  [AdKitKey.RANGE_TEE_OVERLAY]: "Range Tee",
  [AdKitKey.SKILL_EVENT_TEE_OVERLAY]: "Event Tee Overlay",
  [AdKitKey.ROUND_TEE_OVERLAY]: "Round Tee Overlay",
  [AdKitKey.LEADERBOARD]: "Event Leaderboard",
};

const PLACEMENT_WHERE_IT_SHOWS: Record<AdKitKey, string> = {
  [AdKitKey.TEE_SIGN_3D]: "3D sign to the right of the tee",
  [AdKitKey.HOLE_LOAD_AD]: "Center screen, fades in and out as each hole loads",
  [AdKitKey.SCORECARD_AD]: "Bottom middle of the scorecard",
  [AdKitKey.SPONSOR_AD_3_IN_GAME]: "Upper right of course and range screens",
  [AdKitKey.SPONSOR_AD_2_POST_SHOT]: "Lower right of the post-shot screen",
  [AdKitKey.SPONSOR_AD_1_SETUP]: "Top center of most setup screens",
  [AdKitKey.RANGE_FAIRWAY_OVERLAY]:
    "On the ground about 125 yds out (range venues)",
  [AdKitKey.RANGE_TEE_OVERLAY]: "On the ground in front of the range tee",
  [AdKitKey.SKILL_EVENT_TEE_OVERLAY]:
    "On the ground in front of the tee, online CTP/LD",
  [AdKitKey.ROUND_TEE_OVERLAY]: "On the ground in front of the tee, round play",
  [AdKitKey.LEADERBOARD]: "Top of the CTP/LD Classic leaderboard",
};

// Not in the API except the hasDynamicRotation flag (the 10-second case).
// Source: the Ad Spaces Workflows doc, "What the How it changes labels mean".
const PLACEMENT_HOW_IT_CHANGES: Record<AdKitKey, string> = {
  [AdKitKey.TEE_SIGN_3D]: "Changes every hole",
  [AdKitKey.HOLE_LOAD_AD]: "Shows as each hole starts",
  [AdKitKey.SCORECARD_AD]: "Changes when the scorecard opens",
  [AdKitKey.SPONSOR_AD_3_IN_GAME]: "Changes every 10 seconds",
  [AdKitKey.SPONSOR_AD_2_POST_SHOT]: "Changes after every shot",
  [AdKitKey.SPONSOR_AD_1_SETUP]: "Changes every 10 seconds",
  [AdKitKey.RANGE_FAIRWAY_OVERLAY]: "Changes after every shot",
  [AdKitKey.RANGE_TEE_OVERLAY]: "Changes after every shot",
  [AdKitKey.SKILL_EVENT_TEE_OVERLAY]: "Changes after every shot",
  [AdKitKey.ROUND_TEE_OVERLAY]: "Changes after every shot",
  [AdKitKey.LEADERBOARD]: "Changes when the leaderboard shows",
};

// Approximate positions from the design canvas's sample screens; not in the API.
const zone = (
  left: number,
  top: number,
  width: number,
  height: number,
): PlacementScreenZone => ({ left, top, width, height });

const PLACEMENT_SCREEN_ZONES: Record<AdKitKey, PlacementScreenZone> = {
  [AdKitKey.TEE_SIGN_3D]: zone(64, 52, 14, 18),
  [AdKitKey.HOLE_LOAD_AD]: zone(30, 28, 40, 40),
  [AdKitKey.SCORECARD_AD]: zone(32, 74, 36, 16),
  [AdKitKey.SPONSOR_AD_3_IN_GAME]: zone(74, 8, 22, 18),
  [AdKitKey.SPONSOR_AD_2_POST_SHOT]: zone(72, 70, 24, 20),
  [AdKitKey.SPONSOR_AD_1_SETUP]: zone(34, 6, 32, 14),
  [AdKitKey.RANGE_FAIRWAY_OVERLAY]: zone(30, 40, 40, 12),
  [AdKitKey.RANGE_TEE_OVERLAY]: zone(30, 70, 40, 14),
  [AdKitKey.SKILL_EVENT_TEE_OVERLAY]: zone(30, 70, 40, 14),
  [AdKitKey.ROUND_TEE_OVERLAY]: zone(30, 70, 40, 14),
  [AdKitKey.LEADERBOARD]: zone(20, 6, 60, 14),
};

const FALLBACK_SCREEN_ZONE = zone(30, 30, 40, 40);

/** "2:1" / "3.3:1" — one decimal, dropped when it's a whole number. */
export function formatAspectRatioLabel(aspectRatio: number): string {
  const rounded = Math.round(aspectRatio * 10) / 10;
  const label = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1);
  return `${label}:1`;
}

/** "2:1, 1000 × 500 px", or just the ratio when the API has no pixel size. */
export function formatPlacementSize(
  meta: Pick<ClubhouseAdKitMetaDataDto, "width" | "height" | "aspectRatio">,
): string {
  const ratio = formatAspectRatioLabel(meta.aspectRatio);
  return meta.width && meta.height
    ? `${ratio}, ${meta.width} × ${meta.height} px`
    : ratio;
}

/** Pure so every screen can build the same catalog from one GET /metadata call. */
export function buildPlacementCatalog(
  samples: { adKitMetaDatas?: ClubhouseAdKitMetaDataDto[] } | undefined,
): PlacementCatalogEntry[] {
  return (samples?.adKitMetaDatas ?? []).map((meta) => ({
    key: meta.id,
    name: PLACEMENT_NAMES[meta.id] ?? meta.name,
    screenZone: PLACEMENT_SCREEN_ZONES[meta.id] ?? FALLBACK_SCREEN_ZONE,
    whereItShows: PLACEMENT_WHERE_IT_SHOWS[meta.id] ?? meta.description ?? "",
    howItChanges:
      PLACEMENT_HOW_IT_CHANGES[meta.id] ??
      (meta.hasDynamicRotation ? "Changes every 10 seconds" : ""),
    imageSize: formatPlacementSize(meta),
    aspectRatio: meta.aspectRatio,
    width: meta.width ?? undefined,
    height: meta.height ?? undefined,
    isHoleEnabled: meta.isHoleEnabled,
    canAssignToSpecificHoles: meta.canAssignToSpecificHoles ?? false,
    hasDynamicRotation: meta.hasDynamicRotation,
    hasTransitions: meta.hasTransitions,
    defaultFadeIn: meta.defaultFadeIn ?? undefined,
    defaultDisplay: meta.defaultDisplay ?? undefined,
    defaultFadeOut: meta.defaultFadeOut ?? undefined,
  }));
}

export function usePlacementCatalog() {
  const { data, isLoading, isFetching, isError, error } =
    useGetAdKitMetadataQuery();
  const placements = useMemo(() => buildPlacementCatalog(data), [data]);
  return { placements, isLoading, isFetching, isError, error };
}
